
import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navigation from '../../components/feature/Navigation';

export default function Gallery() {
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedImage, setSelectedImage] = useState<string | null>(null);

  const categories = [
    { id: 'all',          name: 'All Photos'   },
    { id: 'trip',         name: 'Trip'         },
    { id: 'sports',       name: 'Sports'       },
    { id: 'student-life', name: 'Student Life' },
    { id: 'practicals',   name: 'Practicals'   },
  ];

  // ── Existing gallery folder → Trip ──────────────────────────────────────
  const galleryModules = import.meta.glob('../../assets/gallery/*.{png,jpg,jpeg,webp,gif}', {
    eager: true, query: '?url', import: 'default'
  });

  const tripImages = Object
    .entries(galleryModules)
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([_, url], idx) => ({
      id: idx + 1,
      src: url as string,
      alt: 'Elite College Trip',
      category: 'trip',
      title: 'Trip',
    }));

  // ── Static remote images → Trip ─────────────────────────────────────────
  const staticImages = [
    { id: 1001, src: 'https://static.readdy.ai/image/e999ec2dea4e4b618de520757ef9db5d/4fb41a6a6d0babc2ca9b962524ef988c.jpeg', alt: 'Elite College Campus',    category: 'trip', title: 'Trip' },
    { id: 1002, src: 'https://static.readdy.ai/image/e999ec2dea4e4b618de520757ef9db5d/5c43cc7f5070c0d38fab39d34d092c88.jpeg', alt: 'Elite College Students',  category: 'trip', title: 'Trip' },
    { id: 1003, src: 'https://static.readdy.ai/image/e999ec2dea4e4b618de520757ef9db5d/baf67523eed2b7d095fe5052669d3123.jpeg', alt: 'College Activities',       category: 'trip', title: 'Trip' },
    { id: 1004, src: 'https://static.readdy.ai/image/e999ec2dea4e4b618de520757ef9db5d/c8f433d87df3574cb493316eeac9e78b.jpeg', alt: 'Elite College Event',      category: 'trip', title: 'Trip' },
    { id: 1005, src: 'https://static.readdy.ai/image/e999ec2dea4e4b618de520757ef9db5d/e01b33b2a671921c6d1bc43ff5a21a9b.jpeg', alt: 'Elite College Community',  category: 'trip', title: 'Trip' },
    { id: 1006, src: 'https://static.readdy.ai/image/e999ec2dea4e4b618de520757ef9db5d/19640e3f3bb5d96fa8a240fd786379c9.jpeg', alt: 'Elite College Foundation', category: 'trip', title: 'Trip' },
    { id: 1007, src: 'https://static.readdy.ai/image/e999ec2dea4e4b618de520757ef9db5d/e3eb271dafb9369832a59a4ad7e95d0a.jpeg', alt: 'Elite College Excellence',  category: 'trip', title: 'Trip' },
  ];

  // ── Sports Images folder → Sports / Student Life / Practicals ───────────
  const sportsModules = import.meta.glob('../../assets/Sports Images/*.{png,jpg,jpeg,webp,gif}', {
    eager: true, query: '?url', import: 'default'
  });

  const sportsFiles = new Set([
    'WhatsApp Image 2026-05-20 at 8.10.21 PM.jpeg',
    'WhatsApp Image 2026-05-20 at 8.10.21 PM (1).jpeg',
    'WhatsApp Image 2026-05-20 at 8.10.21 PM (2).jpeg',
    'WhatsApp Image 2026-05-20 at 8.10.22 PM.jpeg',
    'sports 1.jpeg',
    'sp 2.jpeg',
    'sp 3.jpeg',
    'sp 4.jpeg',
    'WhatsApp Image 2026-05-20 at 8.16.09 PM (1).jpeg',
    'WhatsApp Image 2026-05-20 at 8.16.09 PM (2).jpeg',
  ]);

  const practicalsFiles = new Set([
    'WhatsApp Image 2026-05-20 at 8.10.25 PM (1).jpeg',
    'WhatsApp Image 2026-05-20 at 8.10.26 PM.jpeg',
    'WhatsApp Image 2026-05-20 at 8.10.27 PM.jpeg',
    'WhatsApp Image 2026-05-20 at 8.10.27 PM (1).jpeg',
    'WhatsApp Image 2026-05-20 at 8.16.12 PM.jpeg',
    'WhatsApp Image 2026-05-20 at 8.16.12 PM (1).jpeg',
    'WhatsApp Image 2026-05-20 at 8.16.13 PM.jpeg',
    'WhatsApp Image 2026-05-20 at 8.16.13 PM (2).jpeg',
    'WhatsApp Image 2026-05-20 at 8.16.14 PM.jpeg',
    'WhatsApp Image 2026-05-20 at 8.16.14 PM (1).jpeg',
    'WhatsApp Image 2026-05-20 at 8.16.15 PM (1).jpeg',
  ]);

  const newImages = Object
    .entries(sportsModules)
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([path, url], idx) => {
      const fileName = path.split('/').pop() ?? '';
      const category = sportsFiles.has(fileName)
        ? 'sports'
        : practicalsFiles.has(fileName)
          ? 'practicals'
          : 'student-life';
      const titleMap: Record<string, string> = {
        'sports': 'Sports',
        'practicals': 'Practicals',
        'student-life': 'Student Life',
      };
      return {
        id: 2000 + idx,
        src: url as string,
        alt: `Elite College – ${titleMap[category]}`,
        category,
        title: titleMap[category],
      };
    });

  const mergedImages = [...staticImages, ...tripImages, ...newImages].reverse();

  const filteredImages = selectedCategory === 'all'
    ? mergedImages
    : mergedImages.filter(img => img.category === selectedCategory);

  const currentImageIndex = selectedImage
    ? filteredImages.findIndex(img => img.src === selectedImage)
    : -1;

  const goToNextImage = () => {
    if (currentImageIndex < filteredImages.length - 1) {
      setSelectedImage(filteredImages[currentImageIndex + 1].src);
    }
  };

  const goToPreviousImage = () => {
    if (currentImageIndex > 0) {
      setSelectedImage(filteredImages[currentImageIndex - 1].src);
    }
  };

  const handleKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'ArrowRight') goToNextImage();
    if (e.key === 'ArrowLeft') goToPreviousImage();
    if (e.key === 'Escape') setSelectedImage(null);
  };

  // Add keyboard event listener when modal is open
  useEffect(() => {
    if (selectedImage) {
      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }
  }, [selectedImage, currentImageIndex]);

  return (
    <div className="min-h-screen bg-white">
      <Navigation />

      {/* Hero Section */}
      <section
        className="relative py-20 pt-40 bg-cover bg-center bg-no-repeat"
        style={{
          backgroundImage: `linear-gradient(rgba(0, 122, 61, 0.18), rgba(0, 122, 61, 0.18)), url('https://static.readdy.ai/image/e999ec2dea4e4b618de520757ef9db5d/4c3e0a722471962ce9d857e47b8b4056.jpeg')`
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-white">
          <h1 className="text-5xl md:text-6xl font-bold mb-6">Photo Gallery</h1>
          <p className="text-xl md:text-2xl opacity-90">
            Capturing moments, memories, and milestones at Elite College
          </p>
        </div>
      </section>

      {/* Gallery Filter */}
      <section className="py-8 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap justify-center gap-4">
            {categories.map(category => (
              <button
                key={category.id}
                onClick={() => setSelectedCategory(category.id)}
                className={`px-6 py-3 rounded-full font-medium transition-all whitespace-nowrap cursor-pointer ${
                  selectedCategory === category.id
                    ? 'bg-[#007A3D] text-white'
                    : 'bg-white text-gray-600 hover:bg-[#007A3D] hover:text-white'
                }`}
              >
                {category.name}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Gallery Grid */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {filteredImages.map(image => (
              <div
                key={image.id}
                className="group relative overflow-hidden rounded-lg shadow-lg cursor-pointer transform transition-all duration-300 hover:scale-105"
                onClick={() => setSelectedImage(image.src)}
              >
                <img
                  src={image.src}
                  alt={image.alt}
                  className="w-full h-64 object-cover object-center transition-transform duration-300 group-hover:scale-110"
                  loading="lazy"
                  decoding="async"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <div className="absolute bottom-4 left-4 right-4">
                    <h3 className="text-white font-semibold text-lg">{image.title}</h3>
                    <div className="flex items-center justify-between mt-2">
                      <span className="text-white/80 text-sm capitalize">{image.category}</span>
                      <i className="ri-zoom-in-line text-white text-xl"></i>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Image Modal */}
      {selectedImage && (
        <div className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4">
          <div className="relative max-w-4xl max-h-full">
            {/* Close Button */}
            <button
              onClick={() => setSelectedImage(null)}
              className="absolute -top-12 right-0 text-white hover:text-[#C6A44C] cursor-pointer z-10"
            >
              <i className="ri-close-line text-3xl"></i>
            </button>

            {/* Previous Button */}
            {currentImageIndex > 0 && (
              <button
                onClick={goToPreviousImage}
                className="absolute left-4 top-1/2 transform -translate-y-1/2 text-white hover:text-[#C6A44C] cursor-pointer z-10 bg-black/50 rounded-full p-3 transition-all hover:bg-black/70"
              >
                <i className="ri-arrow-left-line text-2xl"></i>
              </button>
            )}

            {/* Next Button */}
            {currentImageIndex < filteredImages.length - 1 && (
              <button
                onClick={goToNextImage}
                className="absolute right-4 top-1/2 transform -translate-y-1/2 text-white hover:text-[#C6A44C] cursor-pointer z-10 bg-black/50 rounded-full p-3 transition-all hover:bg-black/70"
              >
                <i className="ri-arrow-right-line text-2xl"></i>
              </button>
            )}

            {/* Image */}
            <img
              src={selectedImage}
              alt="Gallery image"
              className="max-w-full max-h-full object-contain rounded-lg"
            />

            {/* Image Counter */}
            <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 text-white bg-black/50 px-4 py-2 rounded-full">
              <span className="text-sm">
                {currentImageIndex + 1} / {filteredImages.length}
              </span>
            </div>

            {/* Image Info */}
            {filteredImages[currentImageIndex] && (
              <div className="absolute bottom-4 left-4 text-white">
                <h3 className="text-lg font-semibold mb-1">
                  {filteredImages[currentImageIndex].title}
                </h3>
                <span className="text-sm text-white/80 capitalize">
                  {filteredImages[currentImageIndex].category}
                </span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Call to Action */}
      <section className="py-16 bg-[#005A2C] text-white">
        <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold mb-4">Be Part of Our Story</h2>
          <p className="text-xl mb-8 opacity-90">
            Join Elite College and create your own memorable moments
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/admissions"
              className="bg-[#007A3D] hover:bg-[#C6A44C] text-white px-8 py-4 rounded-lg font-semibold text-lg transition-colors whitespace-nowrap cursor-pointer"
            >
              Apply Now
            </Link>
            <Link
              to="/contact"
              className="bg-[#C6A44C] hover:bg-[#007A3D] text-white px-8 py-4 rounded-lg font-semibold text-lg transition-colors whitespace-nowrap cursor-pointer"
            >
              Schedule a Visit
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#005A2C] text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div>
              <div className="flex items-center mb-4">
                <img
                  src="https://static.readdy.ai/image/e999ec2dea4e4b618de520757ef9db5d/995999b745e34f0ed4248820e32af9bb.jpeg"
                  alt="Elite College Logo"
                  className="h-10 w-10 object-cover rounded-full"
                />
                <h3 className="text-xl font-bold ml-3" style={{ fontFamily: '"Pacifico", serif' }}>
                  Elite College
                </h3>
              </div>
              <p className="text-gray-300 mb-4">
                Empowering tomorrow's leaders through excellence in education and holistic
                development.
              </p>
              <div className="flex space-x-4">
                <button className="text-[#C6A44C] hover:text-white cursor-pointer">
                  <i className="ri-facebook-fill text-xl"></i>
                </button>
                <button className="text-[#C6A44C] hover:text-white cursor-pointer">
                  <i className="ri-twitter-fill text-xl"></i>
                </button>
                <button className="text-[#C6A44C] hover:text-white cursor-pointer">
                  <i className="ri-instagram-line text-xl"></i>
                </button>
                <button className="text-[#C6A44C] hover:text-white cursor-pointer">
                  <i className="ri-linkedin-fill text-xl"></i>
                </button>
                <a href="https://wa.me/263774872973" target="_blank" rel="noopener noreferrer" className="text-[#C6A44C] hover:text-white cursor-pointer">
                  <i className="ri-whatsapp-line text-xl"></i>
                </a>
              </div>
            </div>

            <div>
              <h4 className="text-lg font-semibold mb-4">Quick Links</h4>
              <ul className="space-y-2">
                <li>
                  <Link
                    to="/about"
                    className="text-gray-300 hover:text-[#C6A44C] cursor-pointer transition-colors"
                  >
                    About Us
                  </Link>
                </li>
                <li>
                  <Link
                    to="/admissions"
                    className="text-gray-300 hover:text-[#C6A44C] cursor-pointer transition-colors"
                  >
                    Admissions
                  </Link>
                </li>
                <li>
                  <Link
                    to="/gallery"
                    className="text-gray-300 hover:text-[#C6A44C] cursor-pointer transition-colors"
                  >
                    Gallery
                  </Link>
                </li>
              </ul>
            </div>

            <div>
              <h4 className="text-lg font-semibold mb-4">Contact Info</h4>
              <ul className="space-y-2 text-gray-300">
                <li className="flex items-center">
                  <i className="ri-map-pin-line mr-2 text-[#C6A44C]"></i>
                  535 Mataga
                </li>
                <li className="flex items-center">
                  <i className="ri-phone-line mr-2 text-[#C6A44C]"></i>
                  +263 774 872 973
                </li>
                <li className="flex items-center">
                  <i className="ri-mail-line mr-2 text-[#C6A44C]"></i>
                  info@elitecollege.co.zw
                </li>
              </ul>
            </div>

            <div>
              <h4 className="text-lg font-semibold mb-4">Office Hours</h4>
              <ul className="space-y-2 text-gray-300">
                <li>Monday - Friday: 8:00 AM - 6:00 PM</li>
                <li>Saturday: 9:00 AM - 4:00 PM</li>
                <li>Sunday: Closed</li>
              </ul>
            </div>
          </div>

          <div className="border-t border-[#007A3D] mt-8 pt-8 text-center text-gray-300">
            <p>&copy; 2026 Elite College. All rights reserved.</p>
            <p className="mt-2 text-sm">
              Powered by <a href="https://onhightech.co.zw" target="_blank" rel="noopener noreferrer" className="text-[#C6A44C] hover:text-white underline cursor-pointer transition-colors">Onhightech Solutions</a>
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
